import axios, { AxiosInstance } from 'axios';

/**
 * Production URL layout (per IMPLEMENTATION §15 — two-host split):
 *   plyez.com         → static SPA  (this file is bundled into it)
 *   api.plyez.com     → NestJS backend on 127.0.0.1:3401
 *
 * So the axios client points at `VITE_API_URL` (which is `https://api.plyez.com`
 * in production, `http://localhost:3401` in local dev). The `/api` suffix is
 * added here so callers can keep using short paths like `/workspaces/${id}/...`
 * — they don't have to remember the global prefix. Routes excluded from the
 * Nest global prefix (auth/google, auth/apple, /health, /share, /scim) are
 * called via separate helpers below, not through this axios instance.
 */
const ORIGIN = ((import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3401')
  .replace(/\/$/, '');

/** Backend origin without any path suffix. Use for OAuth links + raw fetches. */
export const API_ORIGIN = ORIGIN;

function build(): AxiosInstance {
  const client = axios.create({
    baseURL: `${ORIGIN}/api`,
    withCredentials: true,
    timeout: 30_000,
  });
  client.interceptors.response.use(
    (r) => r,
    (err) => {
      if (err?.response?.status === 401) {
        if (!window.location.pathname.startsWith('/onboarding')) {
          // redirect to landing for fresh OAuth
          window.location.assign('/?auth=required');
        }
      }
      // Plan-limit 403s carry { currentPlan, upgradeTo } so the UI can
      // route to /pricing with the upgrade target pre-selected. We
      // re-throw with the structured payload preserved on err.upgrade.
      if (err?.response?.status === 403 && err.response.data?.error === 'PlanLimitExceeded') {
        const data = err.response.data as { currentPlan: string; upgradeTo: string; message: string };
        (err as { upgrade?: typeof data }).upgrade = {
          currentPlan: data.currentPlan, upgradeTo: data.upgradeTo, message: data.message,
        };
      }
      return Promise.reject(err);
    },
  );
  return client;
}

export const api = build();
